import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge, Banner, Button, Combobox, Input, Label, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Sparkles, Textarea,
} from "bizpandit-ui";
import { apiErrorMessage } from "@/api/apiError";
import { assistantService, tokens, type AssistantProvider, type AssistantStatus, type ChatTurn } from "./assistant.service";

/**
 * The in-app assistant (decision #21) — a panel on the right.
 *
 * The conversation lives in this component and nowhere else: closing the tab
 * ends it, and the server keeps counts, never words. Each turn sends the text
 * so far; the server makes any tool calls afresh, as this person.
 */

const ANTHROPIC_MODELS = [
  { value: "claude-sonnet-5-5", label: "Claude Sonnet 5.5" },
  { value: "claude-opus-5-5", label: "Claude Opus 5.5" },
  { value: "claude-haiku-5-5", label: "Claude Haiku 5.5" },
];

export function KeyForm({
  providers, busy, error, submitLabel, onSubmit,
}: {
  providers: AssistantStatus["providers"];
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (v: { provider: AssistantProvider; model?: string; apiKey: string }) => void;
}) {
  const [provider, setProvider] = useState<AssistantProvider>("ANTHROPIC");
  const [model, setModel] = useState("claude-sonnet-5-5");
  const [apiKey, setApiKey] = useState("");
  const isOpenAi = provider === "OPENAI";
  const canSave = apiKey.trim().length >= 8 && model.trim().length > 0 && !busy;
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) onSubmit({ provider, model: model.trim(), apiKey: apiKey.trim() });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label>Provider</Label>
        <Combobox
          className="w-full"
          options={providers.map((p) => ({ value: p.key, label: p.label }))}
          value={provider}
          onChange={(v) => {
            const p = v as AssistantProvider;
            setProvider(p);
            setModel(providers.find((x) => x.key === p)?.defaultModel ?? "");
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Model</Label>
        {isOpenAi ? (
          <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="The OpenAI model name your key can use" />
        ) : (
          <Combobox className="w-full" options={ANTHROPIC_MODELS} value={model} onChange={(v) => setModel(v)} />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>API key</Label>
        <Input
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={isOpenAi ? "sk-…" : "sk-ant-…"}
        />
        <p className="m-0 text-[11.5px] text-slate-500">
          An API key from {isOpenAi ? "platform.openai.com" : "console.anthropic.com"} — a chat subscription cannot be
          used by another app. It is checked with the provider, stored encrypted, and never shown again.
        </p>
      </div>
      {error && <Banner tone="danger">{error}</Banner>}
      <Button type="submit" disabled={!canSave}>{busy ? "Checking the key…" : submitLabel}</Button>
    </form>
  );
}

export default function AssistantPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["assistant-status"], queryFn: assistantService.status, enabled: open });
  const [turns, setTurns] = useState<Array<ChatTurn & { tools?: Array<{ name: string; ok: boolean }> }>>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  // A block body, never an expression: newer browsers return a Promise from
  // scrollIntoView, and an effect that returns one crashes the whole route on
  // unmount ("destroy is not a function") — found in the browser, not by tsc.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [turns]);

  const connect = useMutation({
    mutationFn: assistantService.connect,
    onSuccess: (s) => { qc.setQueryData(["assistant-status"], s); setKeyError(null); setReplacing(false); },
    onError: (e) => setKeyError(apiErrorMessage(e, "The key could not be saved.")),
  });
  const disconnect = useMutation({
    mutationFn: assistantService.disconnect,
    onSuccess: (s) => qc.setQueryData(["assistant-status"], s),
    onError: (e) => setError(apiErrorMessage(e, "Could not disconnect.")),
  });
  const send = useMutation({
    mutationFn: (history: ChatTurn[]) => assistantService.chat(history),
    onSuccess: (r) => {
      setTurns((t) => [...t, { role: "assistant", text: r.reply, tools: r.tools }]);
      setError(null);
      if (r.usage.source === "COMPANY_KEY") void qc.invalidateQueries({ queryKey: ["assistant-status"] });
    },
    onError: (e) => {
      setError(apiErrorMessage(e, "The assistant could not answer."));
      void qc.invalidateQueries({ queryKey: ["assistant-status"] });
    },
  });

  const submit = () => {
    const text = draft.trim();
    if (!text || send.isPending) return;
    const next = [...turns, { role: "user" as const, text }];
    setTurns(next);
    setDraft("");
    // The server takes at most 40 turns; the oldest drop off first.
    send.mutate(next.slice(-40).map(({ role, text }) => ({ role, text })));
  };

  const s = status.data;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-rose" /> Assistant</SheetTitle>
          <SheetDescription>
            Works with your own permissions — it can see and do only what you can.
          </SheetDescription>
        </SheetHeader>

        {status.isLoading && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
        {status.isError && <Banner tone="danger" className="mt-4">{apiErrorMessage(status.error, "The assistant is not available.")}</Banner>}

        {s && !s.workspaceEnabled && (
          <Banner tone="info" className="mt-4" title="Not switched on">
            The assistant is switched off for this workspace. An administrator can turn it on in Settings → Integrations.
          </Banner>
        )}

        {s && s.workspaceEnabled && (!s.ready || replacing) && s.byokAllowed && (
          <div className="mt-4 flex flex-col gap-3">
            {!replacing && s.reason && <Banner tone="info">{s.reason}</Banner>}
            <KeyForm
              providers={s.providers}
              busy={connect.isPending}
              error={keyError}
              submitLabel={replacing ? "Replace my key" : "Connect"}
              onSubmit={(v) => connect.mutate(v)}
            />
            {replacing && <Button variant="outline" onClick={() => setReplacing(false)}>Cancel</Button>}
          </div>
        )}
        {s && s.workspaceEnabled && !s.ready && !s.byokAllowed && <Banner tone="info" className="mt-4">{s.reason}</Banner>}

        {s?.ready && !replacing && (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px] text-slate-500">
              {s.using === "OWN_KEY" && s.connection && (
                <>
                  <Badge variant="neutral" soft>your {s.connection.provider === "ANTHROPIC" ? "Anthropic" : "OpenAI"} key ••{s.connection.last4}</Badge>
                  <span>{s.connection.model}</span>
                  <button type="button" className="underline" onClick={() => setReplacing(true)}>Replace</button>
                  <button type="button" className="underline" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>Disconnect</button>
                </>
              )}
              {s.using === "COMPANY_KEY" && s.company && (
                <Badge variant="neutral" soft>company allowance: {tokens(s.company.used)} of {tokens(s.company.limit)} this month</Badge>
              )}
              {turns.length > 0 && <button type="button" className="ml-auto underline" onClick={() => { setTurns([]); setError(null); }}>Clear</button>}
            </div>

            <div className="mt-3 flex-1 overflow-y-auto rounded-xl border border-linegray bg-surface p-3">
              {turns.length === 0 && (
                <p className="m-0 text-sm text-slate-500">
                  Ask about your leads, clients, tasks, calendar or leave. Nothing you type here is kept once you close this tab.
                </p>
              )}
              <div className="flex flex-col gap-3">
                {turns.map((t, i) => (
                  <div key={i} className={t.role === "user" ? "ml-8 self-end rounded-xl bg-navy px-3 py-2 text-sm text-white" : "mr-8 rounded-xl border border-linegray bg-white px-3 py-2 text-sm text-navy2"}>
                    <div className="whitespace-pre-wrap break-words">{t.text}</div>
                    {t.tools && t.tools.length > 0 && (
                      <div className="mt-1.5 text-[11px] text-slate-500">
                        used: {t.tools.map((x) => `${x.name}${x.ok ? "" : " (refused)"}`).join(", ")}
                      </div>
                    )}
                  </div>
                ))}
                {send.isPending && <div className="mr-8 rounded-xl border border-linegray bg-white px-3 py-2 text-sm text-slate-500">Working…</div>}
                <div ref={bottom} />
              </div>
            </div>

            {error && <Banner tone="danger" className="mt-3">{error}</Banner>}
            <form className="mt-3 flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <Textarea
                className="min-h-[44px] flex-1"
                rows={2}
                value={draft}
                maxLength={8000}
                placeholder="Ask the assistant…"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
                }}
              />
              <Button type="submit" disabled={!draft.trim() || send.isPending}>Send</Button>
            </form>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
